// Author profiles for the publication. Powers /authors/<slug>/ pages and
// the Person entity referenced by every BlogPosting.

import umairPhoto from "@/assets/umair.png.asset.json";

export interface AuthorSection {
  heading: string;
  body: string[];
  bullets?: string[];
}

export interface Author {
  slug: string;
  name: string;
  role: string;
  photo: string;
  /** Key into EXTRA_MEDIA_VARIANTS for the responsive AVIF/WebP portrait. */
  photoVariant: string;
  bio: string[];
  expertise: string[];
  credentials: string[];
  sameAs: string[];
  email: string;
  /** Long-form profile content rendered below the expertise cards. */
  sections?: AuthorSection[];
  faqs?: { q: string; a: string }[];
}

export const AUTHORS: Author[] = [
  {
    slug: "umair-mansha",
    name: "M. Umair Mansha",
    role: "Founder & Head of Search — Optimize Plus, Austin TX",
    photo: umairPhoto.url,
    photoVariant: "umair",
    bio: [
      "Umair is the founder of Optimize Plus, an Austin-based SEO, AEO, and GEO agency working with small businesses and B2B founders across Texas. He has spent the last decade running technical audits, local search programs, and content systems for operators who need pipeline, not vanity rankings.",
      "His current focus is AI search: how Google AI Overviews, ChatGPT, and Perplexity choose which sources to cite, and how to structure entities, schema, and first-100-word answers so a business becomes one of them.",
      "Every article on this site is written from live engagements — the numbers, screenshots, and playbooks come from client work in Austin, Dallas, Houston, San Antonio, and the surrounding Texas metros.",
    ],
    expertise: [
      "Technical SEO & Core Web Vitals",
      "Local SEO and Google Business Profile",
      "Answer Engine Optimization (AEO)",
      "Generative Engine Optimization (GEO)",
      "Content strategy & topical authority",
      "Digital PR and link acquisition",
    ],
    credentials: [
      "10+ years in organic search",
      "Founder-led engagements across Texas",
      "Semrush, Ahrefs, GSC, GA4, BigQuery practitioner",
    ],
    sameAs: [
      "https://www.linkedin.com/company/optimizplus",
      "https://x.com/optimize_plus",
    ],
    email: "info@optimizeplus.net",
    sections: [
      {
        heading: "How I got here",
        body: [
          "I did not come to search from marketing. I came to it from operations, running websites for businesses where a bad month in organic traffic showed up directly in the schedule — an empty bay at an auto shop, a technician with no dispatch, a clinic with a half-full calendar. That framing has never left. Rankings are an input. Booked work is the output, and the only one that survives a budget review.",
          "Over the last decade that has meant roughly the same set of problems appearing in different clothing: a site Google cannot crawl properly, a service estate fragmented across pages that compete with each other, a Google Business Profile configured by someone who left three years ago, and content written for an imagined searcher rather than the one placing the call.",
          "Optimize Plus exists because most Texas businesses in that position are underserved. The agencies that can genuinely fix the technical layer tend to price for enterprise; the agencies priced for a local operator tend to sell reporting rather than engineering. The practice is built to close that gap: founder-led technical work, delivered at a scale a regional business can actually buy.",
        ],
      },
      {
        heading: "What I actually work on",
        body: [
          "Most engagements start with a technical audit, because most sites are leaking before they are underperforming. Crawl efficiency, indexation, canonical integrity, redirect hygiene, rendering, and Core Web Vitals — unglamorous work that reliably produces the first movement in a programme, usually inside six weeks and usually without publishing a word.",
          "From there it depends on the constraint. In competitive local markets the binding constraint is usually the Google Business Profile and the service-area layer. In B2B and technical niches it is topical coverage and entity clarity. In high-trust categories like medical, legal, and specialist automotive, it is authority — and no amount of on-page work substitutes for earned citation.",
          "The part of the job I find most interesting right now is AI search. Google AI Overviews, ChatGPT, Perplexity, and Copilot all choose sources differently from classic ranking, and the deciding factors are unusually tractable: does the page answer the question in the first hundred words, are the entities unambiguous, is the claim specific enough to be quotable, and does anything off-site corroborate it. That is a solvable engineering problem, and most sites have not started on it.",
        ],
        bullets: [
          "Technical SEO audits: crawl, indexation, canonicals, rendering, Core Web Vitals",
          "Local SEO and Google Business Profile programmes for service-area businesses",
          "Answer Engine Optimization — structuring pages for featured snippets and AI Overviews",
          "Generative Engine Optimization — earning citation in ChatGPT, Perplexity, and Copilot",
          "Topical authority architecture: cluster design, internal linking, entity modelling",
          "Digital PR and link acquisition in trust-heavy verticals",
        ],
      },
      {
        heading: "How I write and what I refuse to publish",
        body: [
          "Everything on this site comes out of live engagements. If an article contains a number, that number came from a Search Console property, a Semrush export, or a client's analytics — not from an industry roundup. If it contains a playbook, I have run it, including the parts that did not work.",
          "I do not publish predictions dressed as advice, and I do not publish the same 'ten SEO tips' article the industry recycles every quarter. Where I am uncertain, I say so. Where a tactic worked once and I cannot yet tell whether it generalises, I say that too. The publication is meant to be useful to someone doing the work, which means it has to be honest about failure modes.",
          "The practical test I apply before publishing: would this article change what a competent practitioner does on Monday morning? If the answer is no, it does not ship.",
        ],
      },
      {
        heading: "Where I work",
        body: [
          "Optimize Plus is based in Austin and the bulk of the client base sits in Central Texas — Austin, Round Rock, Cedar Park, Georgetown, Pflugerville, San Marcos, New Braunfels — with ongoing work in San Antonio, Houston, Dallas–Fort Worth, and the Bryan–College Station corridor.",
          "Texas markets are not interchangeable. Austin's competitive density in tech-adjacent services is closer to a coastal metro than to the rest of the state; Houston fragments across an enormous geography where suburb-level relevance beats metro-level authority; Dallas–Fort Worth splits into two distinct search markets that many agencies treat as one. Understanding those differences is most of what local SEO actually is.",
        ],
      },
      {
        heading: "How engagements run",
        body: [
          "Founder-led, which means I do the technical work rather than sell it and hand it off. That constrains how many clients the practice can carry, and it is a deliberate constraint. The alternative — a junior executing an audit template against a business they have never spoken to — is how most local SEO retainers quietly fail.",
          "Reporting is built around the questions an owner actually asks: which pages are producing enquiries, what changed this month and why, and what is being shipped next. Ranking screenshots are available on request and are never the headline.",
        ],
      },
    ],
    faqs: [
      {
        q: "What does Optimize Plus specialise in?",
        a: "Technical SEO, local SEO for service-area businesses, and AI search optimisation (AEO and GEO) for small businesses and B2B founders across Texas. The common thread is engineering-led work rather than reporting-led retainers.",
      },
      {
        q: "Do you work with businesses outside Texas?",
        a: "Yes, though the majority of the client base is Central Texas. Technical SEO and AI search work are geography-independent; local SEO programmes benefit from market familiarity, which is why Texas is the focus.",
      },
      {
        q: "What is the difference between SEO, AEO, and GEO?",
        a: "SEO earns a ranking position in the classic results. AEO earns the direct answer — featured snippets and AI Overviews. GEO earns citation inside generative assistants like ChatGPT and Perplexity. They share a foundation but the optimisation targets differ, particularly around answer placement and entity clarity.",
      },
      {
        q: "How long before an engagement shows results?",
        a: "Technical remediation typically produces the first measurable movement in four to eight weeks. Local pack presence usually arrives around month three. Content and authority programmes compound over two to four quarters. Anyone promising page-one head terms in thirty days is selling something else.",
      },
      {
        q: "Are the case study numbers real?",
        a: "Yes, and every figure is attributed to its source and capture date — Google Search Console or Semrush — on the case study and portfolio pages. Where a client has asked for anonymity, the metrics are published and the name is not.",
      },
    ],
  },
];

export const getAuthor = (slug: string) => AUTHORS.find((a) => a.slug === slug);
